import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useWizard } from "@/lib/wizard";
import { useI18n, tCountry } from "@/lib/i18n";
import {
  Globe2, ThermometerSun, Building2, Layers, Box, Check, ChevronRight, ChevronDown,
  DollarSign, TrendingUp, Ruler,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import InteractiveSuppliersMap from "@/components/portal/InteractiveSuppliersMap";
import { CATEGORY_META, CATEGORIZED_MATERIALS } from "@/lib/materialCatalog";

const NEON_CYAN  = "#00E5FF";
const NEON_GREEN = "#39FF14";
const NEON_AMBER = "#FFCC00";
const NEON_PINK  = "#FF2DAA";
const NEON_LIME  = "#B6FF00";
const NEON_VIOLET= "#9D4EDD";
const NEON_BRED  = "#FF1F1F";

const STEPS_META = [
  { key: "country",        labelKey: "wizard.country",        icon: Globe2,         color: NEON_CYAN  },
  { key: "climate",        labelKey: "wizard.climate",        icon: ThermometerSun, color: NEON_GREEN },
  { key: "insulationType", labelKey: "wizard.insulationType", icon: Layers,         color: NEON_PINK  },
  { key: "material",       labelKey: "wizard.materials",      icon: Box,            color: NEON_LIME  },
  { key: "space",          labelKey: "wizard.space",          icon: Building2,      color: NEON_AMBER },
  { key: "boyutlar",       labelKey: "wizard.dimensions",     icon: Ruler,          color: "#00E5FF", action: true },
  { key: "maliyet",        labelKey: "wizard.cost",           icon: DollarSign,     color: NEON_VIOLET, action: true },
  { key: "finans",         labelKey: "wizard.finans",         icon: TrendingUp,     color: NEON_BRED,   action: true },
];

const baseBtn =
  "flex items-center gap-2 px-4 py-3 font-mono text-[13px] uppercase tracking-[0.18em] " +
  "border-2 bg-black text-white font-bold transition-all duration-200 cursor-pointer select-none";

function StepButton({ meta, valueLabel, onClick, t }) {
  const Icon = meta.icon;
  const isAction = !!meta.action;
  const hasValue = !!valueLabel;
  const isFinans = meta.key === "finans";
  // Every button gets its neon border + soft glow at all times (UI consistency).
  // When the user has selected a value (or this is an action button), the glow intensifies.
  const baseGlow   = `0 0 6px ${meta.color}44, inset 0 0 6px ${meta.color}11`;
  const strongGlow = `0 0 14px ${meta.color}88, inset 0 0 8px ${meta.color}22`;
  const lit = isAction || hasValue;
  return (
    <button
      onClick={onClick}
      data-testid={`wizard-btn-${meta.key}`}
      className={baseBtn}
      style={{
        borderColor: meta.color,
        boxShadow: lit ? strongGlow : baseGlow,
        color: "#FFFFFF",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 18px ${meta.color}AA, inset 0 0 10px ${meta.color}33`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = lit ? strongGlow : baseGlow;
      }}>
      <Icon className="w-5 h-5" style={{
        color: meta.color,
        filter: isFinans ? `drop-shadow(0 0 6px ${meta.color})` : "none",
      }}/>
      <span className="flex flex-col items-start leading-tight">
        <span
          className="text-[10px] font-bold tracking-[0.22em]"
          style={{
            color: meta.color,
            textShadow: isFinans
              ? `0 0 8px ${meta.color}, 0 0 14px ${meta.color}88`
              : `0 0 4px ${meta.color}55`,
          }}>
          {t ? t(meta.labelKey, meta.labelKey) : meta.labelKey}
        </span>
        {!isAction && (
          <span className="text-[14px] font-extrabold text-white truncate max-w-[160px]">
            {valueLabel || "—"}
          </span>
        )}
      </span>
      {!isAction && hasValue && <Check className="w-4 h-4" style={{ color: meta.color }}/>}
    </button>
  );
}

export default function WizardBar({ onNavigate }) {
  const { t, lang } = useI18n();
  const { country, setCountry, climate, setClimate, space, setSpace,
          insCategory, setInsCategory,
          insType, setInsType, material, setMaterial, dimensions } = useWizard();
  const [openKey, setOpenKey] = useState(null);
  const [climates, setClimates]     = useState([]);
  const [spaces, setSpaces]         = useState([]);
  const [search, setSearch]         = useState("");
  // Which category section is expanded inside the MATERIAL dialog (defaults to Step-3 choice).
  const [expandedCat, setExpandedCat] = useState(null);

  useEffect(() => {
    api.get("/climates").then(({data}) => setClimates(data)).catch(()=>{});
    api.get("/insulation-spaces").then(({data}) => setSpaces(data)).catch(()=>{});
  }, []);

  const labels = {
    country:        tCountry(country, lang),
    climate:        climate?.name,
    space:          space?.name?.split(" (")[0],
    boyutlar:       `${dimensions.width}×${dimensions.length}×${dimensions.height} m`,
    insulationType: insCategory ? (CATEGORY_META[insCategory]?.labelTr || "") : "",
    material:       material?.name?.split(" (")[0],
  };

  const onOpen = (key) => {
    setSearch("");
    // Action buttons redirect to dedicated tab views
    if (key === "boyutlar")  { onNavigate && onNavigate("boyutlar"); return; }
    if (key === "maliyet")   { onNavigate && onNavigate("maliyet");  return; }
    if (key === "finans")    { onNavigate && onNavigate("ekonomi");  return; }
    if (key === "material") setExpandedCat(insCategory || "ISIMAL");
    setOpenKey(key);
  };
  const close  = () => setOpenKey(null);

  return (
    <div
      className="w-full border-y border-[#1F1F1F] bg-black"
      data-testid="wizard-bar">
      <div className="max-w-[1600px] mx-auto px-3 py-3 flex flex-wrap items-center gap-2">
        {STEPS_META.map((m, i) => (
          <React.Fragment key={m.key}>
            <StepButton meta={m} valueLabel={labels[m.key]} onClick={() => onOpen(m.key)} t={t}/>
            {i < STEPS_META.length - 1 && (
              <ChevronRight className="w-3.5 h-3.5 text-[#3A3A3A]"/>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — ÜLKE · Interactive 197-country dynamic map (replaces static picker) */}
      <PickerDialog open={openKey === "country"} onClose={close}
        title="Select Country · Interactive Map · 197 Nations"
        color={NEON_CYAN} search="" setSearch={()=>{}} hideSearch wide>
        <InteractiveSuppliersMap/>
      </PickerDialog>

      {/* CLIMATE DIALOG */}
      <PickerDialog open={openKey === "climate"} onClose={close} title="Select Climate"
        color={NEON_GREEN} search={search} setSearch={setSearch}>
        <div className="space-y-3">
          {climates.map((parent) => (
            <div key={parent.id}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-2 h-2" style={{ background: parent.color, boxShadow: `0 0 6px ${parent.color}` }}/>
                <span className="font-mono text-[12px] uppercase tracking-[0.22em] font-extrabold"
                  style={{ color: parent.color }}>
                  {parent.name}
                </span>
                <span className="font-mono text-[12px] font-bold text-white">{parent.description}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {parent.sub_climates
                  .filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()))
                  .map((s) => (
                  <button key={s.id}
                    onClick={() => { setClimate({ ...s, parent: parent.name, parentColor: parent.color }); close(); }}
                    data-testid={`climate-${s.id}`}
                    className="text-left p-3 border-2 border-[#1F1F1F] transition-colors bg-black"
                    style={{
                      borderColor: climate?.id === s.id ? parent.color : "#1F1F1F",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = parent.color; }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = climate?.id === s.id ? parent.color : "#1F1F1F";
                    }}>
                    <div className="font-extrabold text-white text-base">{s.name}</div>
                    <div className="font-mono text-[11px] mt-1 font-bold" style={{ color: parent.color }}>
                      HDD {s.hdd_range} · CDD {s.cdd_range}
                    </div>
                    <div className="font-mono text-[12px] mt-1 font-bold text-white">{s.notes}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PickerDialog>

      {/* SPACE DIALOG */}
      <PickerDialog open={openKey === "space"} onClose={close} title="Select Insulation Space"
        color={NEON_AMBER} search={search} setSearch={setSearch}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {spaces.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase())).map((s) => (
            <button key={s.id} onClick={() => { setSpace(s); close(); }}
              data-testid={`space-${s.id}`}
              className="text-left p-3 border border-[#1F1F1F] hover:border-[#FFCC00]
                hover:bg-[#FFCC00]/5 transition-colors">
              <div className="font-bold text-white">{s.name}</div>
              <div className="font-mono text-[10px] mt-1 text-[#FFCC00] uppercase tracking-wider">
                {s.applications.join(" · ")}
              </div>
              <div className="font-mono text-[11px] mt-1 text-white">
                Thermal bridge: <span className="text-white/80">{s.thermal_bridges}</span>
              </div>
              <div className="font-mono text-[11px] mt-1 text-white">
                Recommended: <span className="text-[#FFCC00]">{s.recommended_systems.join(", ")}</span>
              </div>
            </button>
          ))}
        </div>
      </PickerDialog>

      {/* STEP 3 — İZOLASYON TÜRÜ · Insulation Type category selector (5 purposes) */}
      <PickerDialog open={openKey === "insulationType"} onClose={close} title="Select Insulation Type · İzolasyon Türü"
        color={NEON_PINK} search="" setSearch={()=>{}} hideSearch>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/80 mb-3">
          Choose the primary purpose of insulation — this pre-filters Step 4 (Material).
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {Object.values(CATEGORY_META).map((cat) => {
            const active = insCategory === cat.code;
            return (
              <button key={cat.code}
                onClick={() => {
                  setInsCategory(cat.code);
                  setExpandedCat(cat.code);
                  close();
                  setTimeout(() => setOpenKey("material"), 200);
                }}
                data-testid={`inscat-${cat.code.toLowerCase()}`}
                className="text-left p-4 border-2 transition-all"
                style={{
                  borderColor: active ? cat.color : "#1F1F1F",
                  background: active ? `${cat.color}18` : "#050505",
                  boxShadow: active ? `0 0 18px ${cat.color}77, inset 0 0 12px ${cat.color}22` : "none",
                }}>
                <div className="text-3xl mb-2">{cat.icon}</div>
                <div className="font-mono font-black tracking-[0.20em] text-lg mb-1"
                  style={{ color: cat.color, textShadow: `0 0 8px ${cat.color}88` }}>
                  {cat.labelTr}
                </div>
                <div className="font-mono text-[10px] uppercase text-white/70">{cat.subtitle}</div>
                <div className="font-mono text-[9px] text-white/50 mt-2">
                  {CATEGORIZED_MATERIALS[cat.code]?.length || 0} materyal
                </div>
              </button>
            );
          })}
        </div>
      </PickerDialog>

      {/* STEP 4 — MALZEME · Categorized material picker (69 items across 5 purposes) */}
      <PickerDialog open={openKey === "material"} onClose={close} title="Select Material · Malzeme"
        color={NEON_LIME} search={search} setSearch={setSearch}>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/80 mb-3">
          {insCategory
            ? <>Auto-filtered by Step 3 : <span style={{ color: CATEGORY_META[insCategory].color }}>{CATEGORY_META[insCategory].subtitle}</span>. All categories browsable below.</>
            : <>Choose an insulation type in Step 3 for auto-filter, or browse all 5 categories below.</>}
        </div>
        <div className="space-y-3">
          {Object.values(CATEGORY_META).map((cat) => {
            const items = CATEGORIZED_MATERIALS[cat.code] || [];
            const filtered = items.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()));
            const isExpanded = expandedCat === cat.code;
            const isActiveCat = insCategory === cat.code;
            return (
              <div key={cat.code}
                data-testid={`matcat-section-${cat.code.toLowerCase()}`}
                className="border-2"
                style={{
                  borderColor: isActiveCat ? cat.color : "#1F1F1F",
                  boxShadow: isActiveCat ? `0 0 12px ${cat.color}55` : "none",
                }}>
                <button
                  onClick={() => setExpandedCat(isExpanded ? null : cat.code)}
                  data-testid={`matcat-hdr-${cat.code.toLowerCase()}`}
                  className="w-full flex items-center justify-between px-3 py-2.5 transition-colors"
                  style={{
                    background: isActiveCat ? `${cat.color}22` : "#0A0A0A",
                  }}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <div className="text-left">
                      <div className="font-mono font-black tracking-[0.18em] text-sm"
                        style={{ color: cat.color, textShadow: `0 0 6px ${cat.color}66` }}>
                        {cat.labelTr}
                      </div>
                      <div className="font-mono text-[9px] uppercase text-white/60">{cat.subtitle}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] px-2 py-0.5"
                      style={{ color: cat.color, border: `1px solid ${cat.color}55`, background: `${cat.color}0F` }}>
                      {items.length}
                    </span>
                    <ChevronDown className={"w-4 h-4 transition-transform " + (isExpanded ? "rotate-180" : "")}
                      style={{ color: cat.color }}/>
                  </div>
                </button>
                {isExpanded && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-2 bg-black">
                    {filtered.length === 0
                      ? <div className="col-span-full font-mono text-[11px] text-white/50 py-2">No match.</div>
                      : filtered.map((m) => (
                        <button key={m.id}
                          onClick={() => {
                            setMaterial(m);
                            if (!insCategory) setInsCategory(cat.code);
                            close();
                            onNavigate && onNavigate("material");
                          }}
                          data-testid={`material-${m.id}`}
                          className="text-left p-3 border transition-all"
                          style={{ borderColor: "#1F1F1F" }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.background = `${cat.color}0A`; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1F1F1F"; e.currentTarget.style.background = "transparent"; }}>
                          <div className="font-bold text-white text-sm leading-tight">{m.name}</div>
                          <div className="font-mono text-[10px] mt-1" style={{ color: cat.color }}>
                            [{cat.labelTr}]
                          </div>
                          <div className="grid grid-cols-3 gap-1 mt-1.5 font-mono text-[10px] text-white/80">
                            <div>λ={m.lambda_value}</div>
                            <div>μ={m.mu}</div>
                            <div>{m.fire_class}</div>
                          </div>
                          <div className="font-mono text-[9px] mt-1 text-white/50 line-clamp-2">
                            {m.note}
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PickerDialog>

    </div>
  );
}

function PickerDialog({ open, onClose, title, color, search, setSearch, children, hideSearch, wide }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={"rounded-none bg-black border-[#1F1F1F] text-white " +
          (wide ? "max-w-[1500px] w-[98vw] max-h-[92vh]" : "max-w-5xl w-[96vw] max-h-[85vh]") +
          " overflow-hidden flex flex-col"}
        style={{ boxShadow: `0 0 22px ${color}55, inset 0 0 12px ${color}22`, borderColor: color }}
        data-testid="wizard-dialog"
        onPointerDownOutside={(e) => {
          // Prevent Radix from auto-closing when the user interacts with
          // the Leaflet map (its tile/marker DOM lives inside the dialog,
          // but Radix sometimes treats SVG layers as 'outside').
          const t = e.target;
          if (t && t.closest && (t.closest(".leaflet-container") || t.closest("[data-testid='country-map']"))) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const t = e.target;
          if (t && t.closest && (t.closest(".leaflet-container") || t.closest("[data-testid='country-map']"))) {
            e.preventDefault();
          }
        }}>
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-[0.2em] flex items-center gap-2"
            style={{ color }}>
            <span className="w-2 h-2 pulse-live" style={{ background: color }}/>
            {title}
          </DialogTitle>
        </DialogHeader>
        {!hideSearch && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="rounded-none bg-black border-[#1F1F1F] text-white placeholder:text-[#5A5A5A]
              focus-visible:ring-0 focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm"
            data-testid="wizard-search"
          />
        )}
        <div className="overflow-y-auto pt-2 pr-1">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}


